import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";

// These imports correspond to the 3 API calls we created:
import { getEmailNotifications } from "@/api/getEmailNotifications/getEmailNotificationsApi";
import {
  GetEmailNotificationsRequest,
  GetEmailNotificationsResponse,
  EmailNotification as EmailNotificationItem,
} from "@/api/getEmailNotifications/types";

import { createEmailNotification } from "@/api/createEmailNotification/createEmailNotificationApi";
import {
  CreateEmailNotificationRequest,
  CreateEmailNotificationResponse,
} from "@/api/createEmailNotification/types";

import { approveEmailNotification } from "@/api/approveEmailNotification/approveEmailNotificationApi";
import {
  ApproveEmailNotificationRequest,
  ApproveEmailNotificationResponse,
} from "@/api/approveEmailNotification/types";

// Slice state
interface EmailNotificationState {
  notifications: EmailNotificationItem[];
  totalCount: number;

  getLoading: boolean;
  getError: string | null;

  createLoading: boolean;
  createError: string | null;

  approveLoading: boolean;
  approveError: string | null;
}

const initialState: EmailNotificationState = {
  notifications: [],
  totalCount: 0,

  getLoading: false,
  getError: null,

  createLoading: false,
  createError: null,

  approveLoading: false,
  approveError: null,
};

export const emailNotificationSlice = createSlice({
  name: "emailNotification",
  initialState,
  reducers: {
    setGetLoading(state, action: PayloadAction<boolean>) {
      state.getLoading = action.payload;
    },
    setGetError(state, action: PayloadAction<string | null>) {
      state.getError = action.payload;
    },
    setNotifications(state, action: PayloadAction<EmailNotificationItem[]>) {
      state.notifications = action.payload;
    },
    setTotalCount(state, action: PayloadAction<number>) {
      state.totalCount = action.payload;
    },

    setCreateLoading(state, action: PayloadAction<boolean>) {
      state.createLoading = action.payload;
    },
    setCreateError(state, action: PayloadAction<string | null>) {
      state.createError = action.payload;
    },

    setApproveLoading(state, action: PayloadAction<boolean>) {
      state.approveLoading = action.payload;
    },
    setApproveError(state, action: PayloadAction<string | null>) {
      state.approveError = action.payload;
    },

    // Optionally, if you'd like to update an individual notification after approving:
    updateNotificationApproved(
      state,
      action: PayloadAction<{ id: number; approved: boolean }>,
    ) {
      const { id, approved } = action.payload;
      const index = state.notifications.findIndex((n) => n.id === id);
      if (index !== -1) {
        state.notifications[index].approved = approved;
      }
    },

    // Could optionally add a helper for adding a newly created notification to the list
    addNewNotification(state, action: PayloadAction<EmailNotificationItem>) {
      state.notifications.unshift(action.payload);
      state.totalCount += 1;
    },
  },
});

export default emailNotificationSlice.reducer;

export const {
  setGetLoading,
  setGetError,
  setNotifications,
  setTotalCount,

  setCreateLoading,
  setCreateError,

  setApproveLoading,
  setApproveError,

  updateNotificationApproved,
  addNewNotification,
} = emailNotificationSlice.actions;

// Thunk: Fetch Email Notifications
export const fetchEmailNotificationsAction =
  (params?: GetEmailNotificationsRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setGetLoading(true));
    dispatch(setGetError(null));
    try {
      const result: GetEmailNotificationsResponse =
        await getEmailNotifications(params);
      dispatch(setNotifications(result.data));
      dispatch(setTotalCount(result.meta?.totalCount ?? 0));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch email notifications.";
      dispatch(setGetError(message));
    } finally {
      dispatch(setGetLoading(false));
    }
  };

// Thunk: Create Email Notification
export const createEmailNotificationAction =
  (payload: CreateEmailNotificationRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setCreateLoading(true));
    dispatch(setCreateError(null));
    try {
      const response: CreateEmailNotificationResponse =
        await createEmailNotification(payload);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to create email notification.";
      dispatch(setCreateError(message));
    } finally {
      dispatch(setCreateLoading(false));
    }
  };

// Thunk: Approve Email Notification
export const approveEmailNotificationAction =
  (payload: ApproveEmailNotificationRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setApproveLoading(true));
    dispatch(setApproveError(null));
    try {
      const response: ApproveEmailNotificationResponse =
        await approveEmailNotification(payload);
      dispatch(
        updateNotificationApproved({
          id: response.data.id,
          approved: response.data.approved,
        }),
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to approve email notification.";
      dispatch(setApproveError(message));
    } finally {
      dispatch(setApproveLoading(false));
    }
  };
